<!--
  ⚠️  PROOF OF CONCEPT CODE - DO NOT USE AS REFERENCE ⚠️

  This component is a proof of concept implementation and does NOT represent
  our coding best practices or standards. The code is ripe for refactoring
  and should be IGNORED by developers and AI models investigating our codebase
  for insights on how we like to structure and write code.

  DO NOT use this as a reference for:
  - Component architecture patterns
  - TypeScript usage patterns
  - Vue composition patterns
  - Code organization
  - Naming conventions
  - Or any other development practices
-->

<script setup lang="ts">
import { computed } from 'vue';
import StrongIcon from '@/assets/svg/job-title/strong-icon.svg?component';
import GoodIcon from '@/assets/svg/job-title/good-icon.svg?component';
import WeakIcon from '@/assets/svg/job-title/weak-icon.svg?component';

interface JobTitleAnalysisResult {
  rating: 'Weak' | 'Good' | 'Strong';
  feedback: string;
}

const props = defineProps<{
  jobTitle: string;
}>();

// Job Title Analyzer with simplified priority-based rules
const JobTitleAnalyzer = {
  // Priority-ordered rules (first matching rule wins)
  rules: [
    // Critical issues (Weak rating)
    {
      name: 'shift_information',
      regex: new RegExp(
        '\\b(1st|2nd|3rd|first|second|third|night|day|evening|overnight|weekend|weekday|rotating)' +
          '\\s*shift\\b|' +
          '\\b(mon|tue|wed|thu|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday)' +
          '\\s*-\\s*(mon|tue|wed|thu|fri|sat|sun|monday|tuesday|wednesday|thursday|friday|saturday|sunday)\\b|' +
          '\\b\\d{1,2}:\\d{2}\\s*(am|pm|AM|PM)?\\s*-\\s*\\d{1,2}:\\d{2}\\s*(am|pm|AM|PM)?\\b|' +
          '\\b\\d{1,2}(am|pm|AM|PM)\\s*-\\s*\\d{1,2}(am|pm|AM|PM)\\b',
        'i',
      ),
      rating: 'Weak',
      message:
        'Remove shift details. Your AI recruiter will discuss schedule during screening to attract more candidates.',
    },
    {
      name: 'compensation_details',
      regex: new RegExp(
        '\\$[\\d,]+|\\$\\d+\\.?\\d*\\s*(\\/hr|\\/hour|per hour|hourly|hr|hour|annually|yearly|per year|k|K)|' +
          '\\$\\d+\\.?\\d*\\s*-\\s*\\$?\\d+\\.?\\d*|' +
          '\\b\\d+\\.?\\d*\\s*(\\/hr|\\/hour|per hour|hourly|hr|hour)\\b|' +
          '\\bsign[\\s-]?on\\s*bonus\\b|\\bretention\\s*bonus\\b|\\bbonus\\b|\\b\\d+k\\s*-\\s*\\d+k\\b',
        'i',
      ),
      rating: 'Weak',
      message:
        'Remove pay from title. Your AI recruiter attracts better candidates by leading with the ' +
        'opportunity, not the price.',
    },
    {
      name: 'too_generic',
      regex: new RegExp(
        '^(operator|technician|associate|specialist|helper|assistant|worker|employee|staff|team member|' +
          'crew member|general labor|laborer|general)$',
        'i',
      ),
      rating: 'Weak',
      message:
        'Too vague. Add specifics like "CNC Operator" or "Warehouse Associate" so candidates know the role.',
    },
    {
      name: 'multiple_roles',
      regex: new RegExp(
        '\\b\\w+\\s*\\/\\s*\\w+\\b.*\\b(tech|operator|specialist|manager|supervisor|coordinator|' +
          'engineer|mechanic|driver|clerk|assistant|repair|maintenance)\\b',
        'i',
      ),
      rating: 'Weak',
      message: 'Pick one role. Quality candidates avoid positions that combine multiple jobs.',
    },

    // Moderate issues (Good rating)
    {
      name: 'excessive_length',
      check: (title: string) => title.length > 40,
      rating: 'Good',
      message:
        'Consider shortening. Long titles get cut off on mobile where most candidates search.',
    },
    {
      name: 'suspicious_random_endings',
      regex: /\s+[a-z]$/i,
      rating: 'Good',
      message:
        'Check for typos or extra characters. Professional titles attract professional candidates.',
    },
    {
      name: 'spaced_hyphens_formatting',
      regex: /\s+–\s+|\s+—\s+|\s+-\s+(?=\w)|(?<=\w)\s+-\s+/,
      rating: 'Good',
      message: 'Remove special characters to keep your title focused and concise.',
    },
    {
      name: 'company_location',
      regex: new RegExp(
        '\\b(LLC|Inc|Corp|Corporation|Company|Co\\.|Ltd|Industries|Manufacturing|Services|Solutions|Group)' +
          '\\b|,\\s*[A-Z]{2}\\b',
        'i',
      ),
      rating: 'Good',
      message: 'Remove company or location info. Your AI recruiter shows these details separately.',
    },
    {
      name: 'internal_codes',
      regex: /\b(level|lvl)\s*[IVX]+\b|\b(level|lvl)\s*\d+\b|#\d+|\[\w+\]|\bR\d{4,}/i,
      rating: 'Good',
      message: 'Remove internal codes like "Level III". Use "Senior" or "Entry Level" instead.',
    },
    {
      name: 'poor_formatting',
      regex: /[A-Z]{4,}|[!@#$%^*()]{2,}|--+|\s{2,}/,
      rating: 'Good',
      message: 'Clean up formatting. Professional titles attract professional candidates.',
    },
    {
      name: 'redundant_words',
      regex: new RegExp(
        '\\b(position|job|opening|opportunity|role|hiring|needed|wanted|available|immediate|urgent|asap)\\b',
        'i',
      ),
      rating: 'Good',
      message: 'Remove words like "Hiring" or "Urgent". They make your company look desperate.',
    },

    // If no issues, check for strong qualities
    {
      name: 'strong_title',
      check: (title: string) => {
        const hasKeywords = new RegExp(
          '\\b(CNC|maintenance|machine|operator|technician|welder|electrician|mechanic|forklift|quality|' +
            'inspector|assembler|production|warehouse|fabricator|machinist|supervisor|manager|engineer|' +
            'plc|hvac|diesel|industrial|manufacturing)\\b',
          'i',
        ).test(title);
        const goodLength = title.length >= 15 && title.length <= 35;
        const hasSpecificity = title.split(/\s+/).length >= 2;
        return hasKeywords && goodLength && hasSpecificity;
      },
      rating: 'Strong',
      message:
        'Perfect! Your AI recruiter will attract quality candidates with this clear, specific title.',
    },
  ],

  analyzeTitle: function (title: string): JobTitleAnalysisResult {
    if (!title || title.trim().length === 0) {
      return {
        rating: 'Weak',
        feedback: 'Please enter a job title.',
      };
    }

    const trimmedTitle = title.trim();

    // Check each rule in priority order
    for (const rule of this.rules) {
      let matches = false;

      if (rule.regex) {
        matches = rule.regex.test(trimmedTitle);
      } else if (rule.check) {
        matches = rule.check(trimmedTitle);
      }

      if (matches) {
        return {
          rating: rule.rating as 'Weak' | 'Good' | 'Strong',
          feedback: rule.message,
        };
      }
    }

    // Default to Good if no rules match
    return {
      rating: 'Good',
      feedback:
        'Solid title. Consider adding a skill or specialization to attract more targeted candidates.',
    };
  },
};

const analysis = computed(() => {
  return JobTitleAnalyzer.analyzeTitle(props.jobTitle);
});

const calloutStyles = computed(() => {
  switch (analysis.value.rating) {
    case 'Strong':
      return {
        container: 'bg-[#F1FCF5] border border-[#DEFAEA]',
        iconColor: 'text-[#2BBA69]',
        ratingColor: 'text-[#1E9B54]',
        messageColor: 'text-[#0F172A]',
      };
    case 'Good':
      return {
        container: 'bg-[#FFFAED] border border-[#FFF4D8]',
        iconColor: 'text-[#CFA432]',
        ratingColor: 'text-[#BC8C10]',
        messageColor: 'text-[#0F172A]',
      };
    case 'Weak':
      return {
        container: 'bg-[#FFF4F4] border border-[#FDE6E6]',
        iconColor: 'text-[#B91F1F]',
        ratingColor: 'text-[#B91F1F]',
        messageColor: 'text-[#0F172A]',
      };
    default:
      return {
        container: 'bg-gray-50 border border-gray-200',
        iconColor: 'text-gray-600',
        ratingColor: 'text-gray-800',
        messageColor: 'text-gray-900',
      };
  }
});

const IconComponent = computed(() => {
  switch (analysis.value.rating) {
    case 'Strong':
      return StrongIcon;
    case 'Good':
      return GoodIcon;
    case 'Weak':
      return WeakIcon;
    default:
      return StrongIcon;
  }
});
</script>

<template>
  <div v-if="jobTitle && jobTitle.trim().length > 0" class="mb-[22px] mt-0">
    <div class="flex items-start gap-2 rounded-xl p-4" :class="calloutStyles.container">
      <component
        :is="IconComponent"
        class="mt-0.5 h-6 w-6 flex-shrink-0"
        :class="calloutStyles.iconColor"
      />
      <div class="min-w-0 flex-1">
        <span class="text-sm font-bold leading-[21px]" :class="calloutStyles.ratingColor">
          {{ analysis.rating }}:
        </span>
        <span class="ml-1 text-sm font-normal leading-[21px]" :class="calloutStyles.messageColor">
          {{ analysis.feedback }}
        </span>
      </div>
    </div>
  </div>
</template>
